import {
  hasDeployService,
  selectAppById,
  selectDatabaseById,
  selectIsAccountOwner,
  selectServiceById,
} from "@app/deploy";
import {
  buildMfaImplementationResourceGroups,
  evaluateCustomerControl,
  fetchCustomerControls,
  updateCustomerControlResource,
  updateCustomerControlResources,
} from "@app/deploy/security-controls";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import {
  appDetailUrl,
  appServiceUrl,
  databaseDetailUrl,
  endpointDetailUrl,
  environmentDetailUrl,
  teamMembersEditUrl,
  teamSsoUrl,
} from "@app/routes";
import {
  BannerMessages,
  Button,
  ButtonLink,
  ButtonLinkExternal,
  tokens,
} from "@app/ui/shared";
import {
  IconCheckCircle,
  IconExclamationCircle,
  IconMinusCircle,
  IconUsers,
} from "@app/ui/shared/icons";
import {
  ComplianceSummary,
  RadialProgress,
} from "@app/ui/shared/security-controls";
import { fetchUser, fetchUsers, selectUsers } from "@app/users";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSecurityControlDetailContext } from "./security-controls-control";

const toLc = (v: unknown) =>
  v === undefined || v === null ? "" : String(v).toLowerCase();
const toStr = (v: unknown) => (v === undefined || v === null ? "" : String(v));
const SSO_DOCS_URL =
  "https://www.aptible.com/docs/core-concepts/security-compliance/authentication/sso";

const RESOURCE_TYPE_ICONS: Record<string, string> = {
  app: "/resource-types/logo-app.png",
  environment: "/resource-types/logo-environment.png",
  account: "/resource-types/logo-environment.png",
  vhost: "/resource-types/logo-vhost.png",
  endpoint: "/resource-types/logo-vhost.png",
  service: "/resource-types/logo-service.png",
};

const DatabaseIcon = ({ resourceId }: { resourceId: string }) => {
  const db = useSelector((s) => selectDatabaseById(s, { id: resourceId }));
  const src = db.type
    ? `/database-types/logo-${db.type}.png`
    : "/resource-types/logo-database.png";
  return <img src={src} className="w-5 h-5 shrink-0" aria-hidden />;
};

const ResourceIcon = ({
  resourceType,
  resourceId,
}: {
  resourceType: string;
  resourceId: string;
}) => {
  const normalized = resourceType.toLowerCase();
  if (normalized === "database" || normalized === "backup") {
    return <DatabaseIcon resourceId={resourceId} />;
  }
  const src = RESOURCE_TYPE_ICONS[normalized];
  if (src) {
    return <img src={src} className="w-5 h-5 shrink-0" aria-hidden />;
  }
  if (
    ["user", "membership", "role", "mfacontrolproxyuser"].includes(normalized)
  ) {
    return (
      <IconUsers
        className="w-5 h-5 shrink-0 text-gray-500"
        variant="sm"
        aria-hidden
      />
    );
  }
  return null;
};

const ServiceTitle = ({
  serviceId,
  resourceHandle,
  resourceId,
}: {
  serviceId: string;
  resourceHandle?: string;
  resourceId?: string;
}) => {
  const service = useSelector((s) => selectServiceById(s, { id: serviceId }));
  const app = useSelector((s) => selectAppById(s, { id: service.appId }));

  if (!hasDeployService(service)) {
    const fallback = resourceHandle || resourceId || serviceId;
    return <span className={tokens.type["normal lighter"]}>{fallback}</span>;
  }

  const link = service.appId
    ? appServiceUrl(service.appId, service.id)
    : databaseDetailUrl(service.databaseId);
  const label = app.handle
    ? `${app.handle} / ${service.processType}`
    : service.processType;
  return (
    <Link to={link} className={tokens.type["table link"]}>
      {label}
    </Link>
  );
};

const ResourceRow = ({
  compliant,
  title,
  description,
  resourceType,
  resourceId,
  action,
}: {
  compliant: boolean | null;
  title: React.ReactNode;
  description: string;
  resourceType?: string;
  resourceId?: string;
  action?: React.ReactNode;
}) => {
  const badgeClass =
    compliant === null
      ? "bg-gray-100 text-gray-400"
      : compliant
        ? "bg-emerald-100 text-emerald-600"
        : "bg-red-100 text-red-600";
  const icon =
    compliant === null ? (
      <IconMinusCircle className="text-gray-500" variant="sm" />
    ) : compliant ? (
      <IconCheckCircle color="#00633F" variant="sm" />
    ) : (
      <IconExclamationCircle color="#EF4444" variant="sm" />
    );

  return (
    <div className="flex items-start justify-between rounded-lg border border-gray-200 px-3 py-2">
      <div className="flex flex-1 items-start gap-3">
        <div
          className={`mt-1 mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${badgeClass}`}
        >
          {icon}
        </div>
        <div>
          <div className="flex items-center gap-2">
            {resourceType && resourceId && (
              <ResourceIcon
                resourceType={resourceType}
                resourceId={resourceId}
              />
            )}
            <p className="font-medium text-gray-900">{title}</p>
          </div>
          <p className={tokens.type["normal lighter"]}>{description}</p>
        </div>
      </div>
      {action}
    </div>
  );
};

type SsoStatus = "enforced" | "configured" | "available" | "loading";

type SecurityControlContextControl = ReturnType<
  typeof useSecurityControlDetailContext
>["control"];

const SsoStatusIcon = ({ status }: { status: SsoStatus }) => {
  if (status === "enforced") {
    return (
      <div className="flex size-20 items-center justify-center rounded-full bg-emerald-100">
        <IconCheckCircle color="#00633F" variant="lg" />
      </div>
    );
  }

  if (status === "configured") {
    return (
      <div className="flex size-20 items-center justify-center rounded-full bg-amber-100">
        <RadialProgress progress={50} radius={32} variant="warn" />
      </div>
    );
  }

  return (
    <div className="flex size-20 items-center justify-center rounded-full bg-red-100">
      <IconExclamationCircle color="#EF4444" variant="lg" />
    </div>
  );
};

const SsoAction = ({
  status,
  isAccountOwner,
  docsUrl,
}: {
  status: SsoStatus;
  isAccountOwner: boolean;
  docsUrl: string;
}) => {
  if (status === "enforced" || status === "loading") {
    return (
      <ButtonLinkExternal href={docsUrl} variant="white">
        More Info
      </ButtonLinkExternal>
    );
  }

  const actionText =
    status === "configured" ? "Enable SSO Enforcement" : "Get Started With SSO";
  const ownerText =
    status === "configured"
      ? "Only Account Owners can enable SSO Enforcement"
      : "Only Account Owners can configure SSO";

  return (
    <div className="flex flex-col items-center justify-center gap-3 md:flex-row">
      {isAccountOwner ? (
        <ButtonLink to={teamSsoUrl()}>{actionText}</ButtonLink>
      ) : (
        <span className={tokens.type["normal lighter"]}>{ownerText}</span>
      )}
      <ButtonLinkExternal href={docsUrl} variant="white">
        More Info
      </ButtonLinkExternal>
    </div>
  );
};

const getSsoStatus = (
  control: SecurityControlContextControl,
  organizationSsoEnforced: boolean,
  isLoadingCustomerControls: boolean,
): SsoStatus => {
  if (organizationSsoEnforced) {
    return "enforced";
  }

  const resources = control.customerControls.flatMap(
    (customerControl) => customerControl.resources,
  );
  const hasReportData =
    control.customerControls.length > 0 ||
    control.cumulativeMetrics.lastEvaluation !== "";

  if (!hasReportData && isLoadingCustomerControls) {
    return "loading";
  }

  if (
    resources.length > 0 ||
    control.cumulativeMetrics.totalCompliant > 0 ||
    control.cumulativeMetrics.progressPercentage > 0
  ) {
    return "configured";
  }

  return "available";
};

const SecurityControlSsoImplementation = () => {
  const { orgId, organizationName, organizationSsoEnforced, control } =
    useSecurityControlDetailContext();
  const isAccountOwner = useSelector((s) => selectIsAccountOwner(s, { orgId }));
  const customerControlsLoader = useLoader(fetchCustomerControls);
  const status = getSsoStatus(
    control,
    organizationSsoEnforced,
    customerControlsLoader.isLoading,
  );
  const docsUrl = control.globalControl.callToAction || SSO_DOCS_URL;

  const statusCopy = {
    enforced: {
      title: "Single Sign On (SSO) is Configured and Enforced",
      description: `${organizationName} users can only log in and access ${organizationName}'s resources by using your configured SAML provider. This allows you to enforce any restrictions allowed by your SSO provider on logins, such as password rotation or use of specific second factors.`,
    },
    configured: {
      title: "Single Sign On (SSO) is Configured, but Not Enforced.",
      description: `${organizationName} users can log in and access ${organizationName}'s resources by using both your configured SAML provider and their individual Aptible credentials. This limits your ability to enforce any restrictions allowed by your SSO provider on logins, such as password rotation or use of specific second factors.`,
    },
    loading: {
      title: "Checking Single Sign On (SSO) Configuration",
      description: `Aptible is checking whether ${organizationName} has a SAML provider configured.`,
    },
    available: {
      title: "Single Sign On (SSO) is Not Configured",
      description:
        "Aptible provides Single Sign On (SSO) for all customers to control access to their Aptible organization's resources through a separate, single authentication service. To use SSO, configure both the SSO provider and Aptible with metadata related to the SAML protocol.",
    },
  }[status];

  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-5 py-4">
        <h3 className="font-semibold text-gray-900">Overall Implementation</h3>
        <p className={tokens.type["normal lighter"]}>{organizationName}</p>
      </div>

      <div className="flex flex-col items-center px-8 py-10 text-center">
        <SsoStatusIcon status={status} />
        <h4 className="mt-6 text-lg font-semibold text-gray-900">
          {statusCopy.title}
        </h4>
        <p className="mt-3 max-w-3xl text-sm text-gray-600">
          {statusCopy.description}
        </p>
        <div className="mt-6">
          <SsoAction
            status={status}
            isAccountOwner={isAccountOwner}
            docsUrl={docsUrl}
          />
        </div>
      </div>
    </section>
  );
};

type Resource =
  SecurityControlContextControl["customerControls"][number]["resources"][number];

const findUserForResource = (
  resource: Resource,
  allUsers: ReturnType<typeof selectUsers>[string][],
) => {
  const rid = toLc(resource.resourceId);
  const rh = toLc(resource.resourceHandle);

  return allUsers.find((u) => {
    return (
      u.id === toStr(resource.resourceHandle) ||
      u.externalId === toStr(resource.resourceHandle) ||
      u.id === toStr(resource.resourceId) ||
      u.externalId === toStr(resource.resourceId) ||
      toLc(u.email) === rid ||
      toLc(u.username) === rid ||
      toLc(u.email) === rh ||
      toLc(u.username) === rh
    );
  });
};

const getUserResourceTitle = (
  resource: Resource,
  allUsers: ReturnType<typeof selectUsers>[string][],
) => {
  const user = findUserForResource(resource, allUsers);
  return user
    ? `${user.name} — ${user.email}`
    : resource.resourceHandle || resource.resourceId || "";
};

const SecurityControlMfaImplementation = () => {
  const dispatch = useDispatch();
  const { control, orgId, scopeIds } = useSecurityControlDetailContext();

  useQuery(fetchUsers({ orgId }));

  const usersTable = useSelector(selectUsers);
  const allUsers = Object.values(usersTable);

  const userIdentifiers = useMemo(() => {
    const ids = new Set<string>();
    allUsers.forEach((u) => {
      if (u.id) ids.add(u.id);
      if (u.externalId) ids.add(u.externalId);
    });
    return Array.from(ids).sort().join(",");
  }, [allUsers]);

  useEffect(() => {
    const handles = new Set<string>();
    control.customerControls.forEach((cc) =>
      cc.resources.forEach((r) => {
        const rType = (
          r.readableResourceType ||
          r.resourceType ||
          ""
        ).toLowerCase();
        if (rType === "user") {
          const h = toStr(r.resourceHandle);
          if (h) handles.add(h);
        }
      }),
    );
    const userIdsSet = new Set(userIdentifiers.split(",").filter(Boolean));
    const need = Array.from(handles).filter((h) => !userIdsSet.has(h));
    if (need.length > 0) {
      need.forEach((h) => dispatch(fetchUser({ userId: h })));
    }
  }, [control.customerControls, userIdentifiers, dispatch]);

  const resourceGroups = useMemo(
    () =>
      buildMfaImplementationResourceGroups(control.customerControls).sort(
        (a, b) =>
          getUserResourceTitle(a.resource, allUsers).localeCompare(
            getUserResourceTitle(b.resource, allUsers),
          ),
      ),
    [control.customerControls, allUsers],
  );

  const onToggleIgnore = (ids: string[], ignored: boolean) => {
    dispatch(
      updateCustomerControlResources({
        ids,
        ignored,
        orgId,
        accountIds: scopeIds,
      }),
    );
  };

  const activeGroups = resourceGroups.filter(
    (group) => !group.resource.ignored,
  );
  const ignoredGroups = resourceGroups.filter(
    (group) => group.resource.ignored,
  );
  const renderGroup = ({
    resource,
    resourceIds,
  }: (typeof resourceGroups)[number]) => {
    const user = findUserForResource(resource, allUsers);
    const title = getUserResourceTitle(resource, allUsers);
    const userLink = user?.id ? teamMembersEditUrl(user.id) : "";

    return (
      <ResourceRow
        key={resourceIds.join(",")}
        compliant={resource.ignored ? null : resource.compliant}
        resourceType={resource.readableResourceType || resource.resourceType}
        resourceId={resource.resourceId}
        title={
          userLink ? (
            <Link to={userLink} className={tokens.type["table link"]}>
              {title}
            </Link>
          ) : (
            title
          )
        }
        description={
          resource.detailsText ||
          (resource.ignored ? "This user is marked as ignored." : "")
        }
        action={
          <Button
            variant="white"
            onClick={() => onToggleIgnore(resourceIds, !resource.ignored)}
          >
            {resource.ignored ? "Stop Ignoring" : "Ignore"}
          </Button>
        }
      />
    );
  };

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
          Cumulative Control Implementation Status
        </h3>
        <p className={`mt-1 ${tokens.type["normal lighter"]}`}>
          Covering users with access to environments in the current report
          scope.
        </p>
        <div className="mt-4">
          <ComplianceSummary metrics={control.cumulativeMetrics} />
        </div>
      </section>

      {resourceGroups.length === 0 ? (
        <p className={tokens.type["normal lighter"]}>
          No user-specific evaluations are available for this control.
        </p>
      ) : (
        <section className="rounded-xl border border-gray-200 bg-gray-50 p-5">
          <h4 className="text-lg font-semibold text-gray-900">Users</h4>
          <div className="mt-4 space-y-4">
            {activeGroups.length > 0 ? (
              <div className="space-y-3">{activeGroups.map(renderGroup)}</div>
            ) : null}

            {ignoredGroups.length > 0 ? (
              <div className="space-y-2">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Ignored Users
                </h5>
                <div className="space-y-3">
                  {ignoredGroups.map(renderGroup)}
                </div>
              </div>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
};

const SecurityControlDefaultImplementation = () => {
  const dispatch = useDispatch();
  const { control, orgId, scopeIds } = useSecurityControlDetailContext();
  const rerunLoader = useLoader(evaluateCustomerControl);
  const [rerunCustomerControlId, setRerunCustomerControlId] = useState("");

  useQuery(fetchUsers({ orgId }));

  const usersTable = useSelector(selectUsers);
  const allUsers = Object.values(usersTable);
  const resourceLinkFor = (
    resource: (typeof control.customerControls)[number]["resources"][number],
    userId?: string,
  ) => {
    const type = toLc(resource.resourceType);
    const readableType = toLc(resource.readableResourceType);
    const resourceId = toStr(resource.resourceId);

    if (type === "account" || readableType === "environment") {
      return resourceId ? environmentDetailUrl(resourceId) : "";
    }
    if (type === "vhost" || readableType === "endpoint") {
      return resourceId ? endpointDetailUrl(resourceId) : "";
    }
    if (type === "app" || readableType === "app") {
      return resourceId ? appDetailUrl(resourceId) : "";
    }
    if (type === "database" || readableType === "database") {
      return resourceId ? databaseDetailUrl(resourceId) : "";
    }
    if (
      type === "mfacontrolproxyuser" ||
      type === "user" ||
      readableType === "user"
    ) {
      return userId ? teamMembersEditUrl(userId) : "";
    }
    return "";
  };

  const userIdentifiers = useMemo(() => {
    const ids = new Set<string>();
    allUsers.forEach((u) => {
      if (u.id) ids.add(u.id);
      if (u.externalId) ids.add(u.externalId);
    });
    return Array.from(ids).sort().join(",");
  }, [allUsers]);

  useEffect(() => {
    const handles = new Set<string>();
    control.customerControls.forEach((cc) =>
      cc.resources.forEach((r) => {
        const rType = (
          r.readableResourceType ||
          r.resourceType ||
          ""
        ).toLowerCase();
        if (rType === "user") {
          const h = toStr(r.resourceHandle);
          if (h) handles.add(h);
        }
      }),
    );
    const userIdsSet = new Set(userIdentifiers.split(",").filter(Boolean));
    const need = Array.from(handles).filter((h) => !userIdsSet.has(h));
    if (need.length > 0) {
      need.forEach((h) => dispatch(fetchUser({ userId: h })));
    }
  }, [control.customerControls, userIdentifiers, dispatch]);

  const onToggleIgnore = (id: string, ignored: boolean) => {
    dispatch(
      updateCustomerControlResource({
        id,
        ignored,
        orgId,
        accountIds: scopeIds,
      }),
    );
  };

  const renderResources = (
    resources: (typeof control.customerControls)[number]["resources"],
    ignored: boolean,
  ) =>
    resources.map((resource) => {
      const rType =
        (resource.readableResourceType || resource.resourceType) ?? "";
      const isUser = rType.toLowerCase() === "user";
      const user =
        isUser && allUsers.length > 0
          ? findUserForResource(resource, allUsers)
          : undefined;
      const title =
        (isUser && user
          ? `${user.name} — ${user.email}`
          : resource.resourceHandle || resource.resourceId) || "";
      const resourceLink = resourceLinkFor(resource, user?.id);
      const isService = rType.toLowerCase() === "service";

      return (
        <ResourceRow
          key={resource.id}
          compliant={ignored ? null : resource.compliant}
          resourceType={rType}
          resourceId={resource.resourceId}
          title={
            isService ? (
              <ServiceTitle
                serviceId={resource.resourceId}
                resourceHandle={resource.resourceHandle}
                resourceId={resource.resourceId}
              />
            ) : resourceLink ? (
              <Link to={resourceLink} className={tokens.type["table link"]}>
                {title}
              </Link>
            ) : (
              title
            )
          }
          description={
            resource.detailsText ||
            (ignored ? "This resource is marked as ignored." : "")
          }
          action={
            <Button
              variant="white"
              onClick={() => onToggleIgnore(resource.id, !ignored)}
            >
              {ignored ? "Stop Ignoring" : "Ignore"}
            </Button>
          }
        />
      );
    });

  const isAnyRerunning =
    rerunLoader.isLoading ||
    control.customerControls.some(
      (customerControl) => customerControl.isEvaluating,
    );
  const onRerun = (customerControlId: string, environmentId: string) => {
    if (isAnyRerunning) {
      return;
    }

    setRerunCustomerControlId(customerControlId);
    dispatch(
      evaluateCustomerControl({
        id: customerControlId,
        orgId,
        accountIds: [environmentId],
      }),
    );
  };

  return (
    <div className="space-y-8">
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
          Cumulative Control Implementation Status
        </h3>
        <p className={`mt-1 ${tokens.type["normal lighter"]}`}>
          Covering environments in the current report scope.
        </p>
        <div className="mt-4">
          <ComplianceSummary metrics={control.cumulativeMetrics} />
        </div>
      </section>

      {control.customerControls.length === 0 ? (
        <p className={tokens.type["normal lighter"]}>
          No environment-specific evaluations are available for this control.
        </p>
      ) : (
        control.customerControls.map((customerControl) => {
          const activeResources = customerControl.resources.filter(
            (resource) => !resource.ignored,
          );
          const ignoredResources = customerControl.resources.filter(
            (resource) => resource.ignored,
          );
          const isCustomerControlRerunning =
            customerControl.isEvaluating ||
            (rerunLoader.isLoading &&
              rerunCustomerControlId === customerControl.id);

          return (
            <section
              key={customerControl.id}
              className="rounded-xl border border-gray-200 bg-gray-50 p-5"
            >
              <div>
                <h4 className="text-lg font-semibold text-gray-900">
                  <Link
                    to={environmentDetailUrl(customerControl.environmentId)}
                    className={tokens.type["table link"]}
                  >
                    {customerControl.environmentHandle}
                  </Link>
                </h4>
                <p className={tokens.type["normal lighter"]}>
                  {customerControl.metrics.lastEvaluationInWords
                    ? `Evaluated ${customerControl.metrics.lastEvaluationInWords}`
                    : "Never evaluated."}
                </p>
              </div>

              {customerControl.isDisabled ? (
                <div className="mt-4 rounded-lg bg-white p-4 text-sm text-gray-700">
                  This control is only available on production environments.{" "}
                  {customerControl.environmentType === "development"
                    ? "Select a production environment to view implementation details."
                    : null}
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <ComplianceSummary metrics={customerControl.metrics} />
                    <Button
                      variant="white"
                      size="xs"
                      onClick={() =>
                        onRerun(
                          customerControl.id,
                          customerControl.environmentId,
                        )
                      }
                      disabled={isAnyRerunning}
                    >
                      {isCustomerControlRerunning ? "Rerunning..." : "Rerun"}
                    </Button>
                  </div>
                  {rerunCustomerControlId === customerControl.id ? (
                    <BannerMessages {...rerunLoader} />
                  ) : null}

                  {activeResources.length === 0 &&
                  !customerControl.isEvaluating ? (
                    <p className="text-sm text-gray-600">
                      No active resources are currently evaluated for this
                      control.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {renderResources(activeResources, false)}
                    </div>
                  )}

                  {ignoredResources.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Ignored Resources
                      </h5>
                      <div className="space-y-3">
                        {renderResources(ignoredResources, true)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
};

export const SecurityControlImplementationTab = () => {
  const { control } = useSecurityControlDetailContext();

  if (control.globalControl.implementationUiComponent === "sso") {
    return <SecurityControlSsoImplementation />;
  }

  if (control.globalControl.implementationUiComponent === "mfa") {
    return <SecurityControlMfaImplementation />;
  }

  return <SecurityControlDefaultImplementation />;
};
